<template>
  <span
    :class="badgeClassObject"
    data-samora
    v-on="$listeners"
  >
    {{ valueToDisplay }}
  </span>
</template>

<script>
import SAMORA_STYLES from '../../../utils/constants/samora-styles.enum';
import SAMORA_KINDS from '../../../utils/constants/samora-kinds.enum';

import { formatNumber } from '../../../utils/utils';

export const BADGE_STYLES = {
  PRIMARY: SAMORA_STYLES.PRIMARY,
  SECONDARY: SAMORA_STYLES.SECONDARY,
  WARNING: SAMORA_STYLES.WARNING,
  DANGER: SAMORA_STYLES.DANGER,
  SUCCESS: SAMORA_STYLES.SUCCESS,
  INACTIVE: SAMORA_STYLES.INACTIVE,
  TERTIARY: SAMORA_STYLES.TERTIARY,
};

export const BADGE_KINDS = {
  DEFAULT: SAMORA_KINDS.DEFAULT,
  ADD: SAMORA_KINDS.ADD,
  REMOVE: SAMORA_KINDS.REMOVE,
};

export default {
  name: 'SamBadge',
  props: {
    /**
     * The badge value to display
     */
    value: {
      type: [Number, String],
      required: true,
      default: '',
    },
    /**
     * The samora style to apply
     * @values primary, secondary, warning, danger, success
     */
    samStyle: {
      type: String,
      required: false,
      default: BADGE_STYLES.PRIMARY,
      validator: (val) => Object.values(BADGE_STYLES).includes(val),
    },
    /**
     * Apply the subtle type
     * @values true, false
     */
    subtle: {
      type: Boolean,
      required: false,
      default: false,
    },
    /**
     * If the Badge should format its value as a number (value must also be a number)
     * @values true, false
     */
    formatNumber: {
      type: Boolean,
      required: false,
      default: false,
    },
    /**
     * Prepend positive or negative modifier to the value
     * @values default, add, remove
     */
    kind: {
      type: String,
      required: false,
      default: BADGE_KINDS.DEFAULT,
      validator: (val) => Object.values(BADGE_KINDS).includes(val),
    },
  },
  computed: {
    badgeClassObject() {
      return [`sam-badge is-${this.samStyle}`, { 'is-subtle': this.subtle }];
    },
    valueToDisplay() {
      const displayValue =
        this.formatNumber && typeof this.value === 'number' ? formatNumber(this.value) : this.value;

      if (this.kind === SAMORA_KINDS.ADD) {
        return `+${displayValue}`;
      }

      if (this.kind === SAMORA_KINDS.REMOVE) {
        return `-${displayValue}`;
      }

      return displayValue;
    },
  },
};
</script>

<style scoped lang="scss">
.sam-badge {
  @extend .text--body-3, .px-2, .d-inline-flex, .flex-center;
  border-radius: $size--6;
  color: $primary--white;

  &.is-primary {
    background-color: $informative--main;

    &.is-subtle {
      color: $informative--main;
      background-color: $informative--surface;
    }
  }

  &.is-secondary {
    background-color: $secondary--dark-violet;

    &.is-subtle {
      color: $secondary--dark-violet;
      background-color: $accessible--dark-violet;
    }
  }

  &.is-tertiary {
    color: $pure--white;
    background-color: $primary--black-rock;
  }

  &.is-warning {
    background-color: $secondary--burning-orange;

    &.is-subtle {
      color: $secondary--burning-orange;
      background-color: $warning--surface;
    }
  }

  &.is-danger {
    background-color: $error--main;

    &.is-subtle {
      color: $error--hover;
      background-color: $error--surface;
    }
  }

  &.is-success {
    background-color: $success--main;

    &.is-subtle {
      color: $success--pressed;
      background-color: $success--surface;
    }
  }

  &.is-inactive {
    background-color: $neutral--30 !important;
    color: $neutral--60 !important;
  }
}
</style>
